import { useState, useMemo, useRef, useEffect } from "react";
import { useModel } from "../ModelProvider";
import { r2 } from "../engines/computeIS";
import { groupByYear, getYear, formatMonthLabel } from "../engines/dateUtils";
import { quarterColumns } from "../utils/quarterColumns";
import { yearLabel } from "../utils/yearColumns";   // a partial year says so: "2026 (Oct-Dec)" (2026-11-02)
import OnboardingIncomplete from "../components/OnboardingIncomplete";
import ModelLoadError from "../components/ModelLoadError";

/* INCOME STATEMENT MODULE - Live Cross-Module Linkage
   Actuals from DB, forecast computed live from Payroll/Vendor/D&A engines
   All calculations use r2() rounding at final result */

const fmt = n => n.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtK = n => {
  if (n === 0) return "—";
  const s = Math.abs(n) >= 1000
    ? Math.abs(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})
    : Math.abs(n).toFixed(2);
  return n < 0 ? `(${s})` : s;
};
const pct = (num, den) => den === 0 ? "—" : ((num / den) * 100).toFixed(1) + "%";



const FONT = "'DM Sans',system-ui,sans-serif";
const MONO = "'Space Mono',monospace";
const BLUE = "#2A6DB0";
const BLACK = "#1A1C2E";
const SL = { position:"sticky",left:0,zIndex:2,background:"#fff",borderRight:"2px solid #E2E5F0" };




export default function IncomeStatementPage() {
  /* ⓘ PART C: `implementationLines` was dropped from this destructure with the splice that
   * used it — buildISLines now emits the rev_impl_<id> leaf, so this page needs the rows
   * for nothing. Left in, it would be an orphaned binding (a new lint warning). */
  const { isData: data, cutoff, loading, ensureFresh, rawData, onboardingGap, modelState, modelError } = useModel();
  const allMonths = useMemo(() => rawData?.allMonths || [], [rawData?.allMonths]);
  const companyName = rawData?.companyName || '';
  const [collapsed, setCollapsed] = useState(new Set(["rev_header","cogs_header"]));
  const [viewMode, setViewMode] = useState("monthly");
  const tableRef = useRef(null);

  useEffect(() => { ensureFresh(); }, [ensureFresh]);


  // Cutoff picker options: all months in the timeline

  // Sub-phase 4 (page sweep): yearBands derived from groupByYear(allMonths) — drives both
  // the columns useMemo annual path AND the thead year-band row. Replaces hardcoded
  // [2024,2025,2026] iteration.
  const yearBands = useMemo(() => {
    const groups = groupByYear(allMonths);
    return Object.keys(groups).sort().map(y => ({ year: +y, months: groups[y] }));
  }, [allMonths]);
  const lastYear = yearBands.length ? yearBands[yearBands.length - 1].year : null;

  // ── Column definitions based on viewMode ──
  const columns = useMemo(() => {
    if (viewMode === "monthly") {
      return allMonths.map(m => ({ key: m, label: formatMonthLabel(m, "short"), months: [m], year: getYear(m) }));
    } else if (viewMode === "quarterly") {
      return quarterColumns(allMonths, { label: "short" });
    } else { // annual
      return yearBands.map(b => ({ key: String(b.year), label: yearLabel(b.year, b.months), months: b.months, year: b.year }));
    }
  }, [viewMode, allMonths, yearBands]);

  // Aggregate a line's value across a set of months
  const aggVal = (lineId, months) => {
    if (!data[lineId]) return 0;
    return r2(months.reduce((s, m) => s + (data[lineId]?.[m] || 0), 0));
  };

  // Aggregate margin: sum numerator / sum denominator across months
  const aggPct = (numId, denId, months) => {
    const num = aggVal(numId, months);
    const den = aggVal(denId, months);
    return pct(num, den);
  };

  // Is a column forecast? (all its months are forecast)
  const colIsForecast = col => col.months.every(m => m > cutoff);
  // Is a column actuals? (all its months are actuals)
  const colIsActuals = col => col.months.every(m => m <= cutoff);
  // Color for a column
  const colColor = col => colIsForecast(col) ? BLACK : colIsActuals(col) ? BLUE : "#5B4B8A";

  // Year boundaries for vertical separators
  const isYearEnd = col => {
    const lastM = col.months[col.months.length - 1];
    return lastM.endsWith("-12") && col.year !== lastYear;
  };

  const toggle = id => setCollapsed(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  /* W-089 (2026-10-01): THE MISC SPLICE IS GONE, as the implementation splice went in Part C (09-14). buildISLines now
   * emits an unrouted misc line's rev_misc_<id> leaf itself, last in the revenue section (immediately before
   * total_revenue, where this splice put it), so the skeleton IS the layout. Splicing here too would add a DUPLICATE ROW. */
  const allLines = useMemo(() => rawData?.isLines || [], [rawData?.isLines]);

  // Simple approach: for each header, find all lines between it and its matching total.
  // Those are the "children" that get hidden when the header collapses.
  // The header itself is also hidden; only the total remains visible.
  const { sections, totalToHeader } = useMemo(() => {
    // Step 1: pair each header with its matching total by scanning forward
    const pairs = []; // [{headerId, headerIdx, totalId, totalIdx}]
    const headerStack = []; // indices into allLines

    allLines.forEach((line, i) => {
      if (line.type === "header") {
        headerStack.push(i);
      } else if (line.type === "total") {
        // Match to the last header on stack that has level <= this total's level
        for (let s = headerStack.length - 1; s >= 0; s--) {
          if (allLines[headerStack[s]].level <= line.level) {
            pairs.push({
              headerId: allLines[headerStack[s]].id,
              headerIdx: headerStack[s],
              totalId: line.id,
              totalIdx: i,
            });
            headerStack.splice(s, 1);
            break;
          }
        }
      }
    });

    // Step 2: for each pair, children = every line between header and total (exclusive of total)
    // including the header itself
    const sections = {};
    pairs.forEach(p => {
      const children = [];
      for (let i = p.headerIdx; i < p.totalIdx; i++) {
        children.push(allLines[i].id);
      }
      sections[p.headerId] = { children, totalId: p.totalId };
    });

    const t2h = {};
    pairs.forEach(p => { t2h[p.totalId] = p.headerId; });

    return { sections, totalToHeader: t2h };
  }, [allLines]);

  // A line is hidden if ANY section that contains it (in its children list) is collapsed
  // BUT: if a sub-section within that collapsed section is expanded, its children are shielded
  const isHidden = (lineId) => {
    for (const [headerId, sec] of Object.entries(sections)) {
      if (!collapsed.has(headerId)) continue;
      if (!sec.children.includes(lineId)) continue;

      // This line is a child of a collapsed section.
      // Check if it's shielded by an expanded sub-section:
      // A sub-section shields this line if:
      //   - the sub-section's header is NOT collapsed
      //   - this line is within the sub-section's children or is the sub-section's total
      let shielded = false;
      for (const [subHid, subSec] of Object.entries(sections)) {
        if (subHid === headerId) continue;
        if (!sec.children.includes(subHid)) continue; // sub-section must be inside parent
        if (collapsed.has(subHid)) {
          // Sub-section is collapsed: only its total is shielded
          if (lineId === subSec.totalId) { shielded = true; break; }
        } else {
          // Sub-section is expanded: all its children + total are shielded
          if (subSec.children.includes(lineId) || lineId === subSec.totalId) {
            shielded = true; break;
          }
        }
      }
      if (!shielded) return true;
    }
    return false;
  };

  // (annualTotal replaced by aggVal above)

  const renderRow = (line) => {
    if (isHidden(line.id)) return null;
    // Skip conditional lines (bonuses/commissions) if all months are zero
    if (line.conditional && data[line.id]) {
      const hasData = allMonths.some(m => (data[line.id][m] || 0) !== 0);
      if (!hasData) return null;
    }

    const isHeader = line.type === "header";
    const isTotal = line.type === "total";
    const isCalc = line.type === "calc";
    const isMargin = line.type === "margin";
    const isBoldRow = isTotal || isCalc;
    const isTopLevel = line.level === 0;
    const indent = isHeader ? line.level * 16 : (line.level * 16) + 8;

    // Section totals and key calcs
    const keyLines = new Set(["total_revenue","total_cogs","gross_profit","total_sga","operating_income","total_oie","net_income","ebitda"]);
    const isKeyLine = keyLines.has(line.id);

    // Find if this total's parent header is collapsed (so we can show expand arrow)
    let collapsedParentId = null;
    if (isTotal && totalToHeader[line.id] && collapsed.has(totalToHeader[line.id])) {
      collapsedParentId = totalToHeader[line.id];
    }
    // Find matching header for any total (for toggle on click)
    const matchingHeaderId = isTotal ? totalToHeader[line.id] || null : null;

    const isClickable = isHeader || (isTotal && matchingHeaderId);

    // Row styles
    const rowBg = isKeyLine ? "#F7F8FC" : isHeader && isTopLevel ? "#FAFBFE" : "#fff";
    const borderTop = isKeyLine ? "2px solid #E2E5F0" : "none";
    const borderBottom = isKeyLine ? "1px solid #E2E5F0" : "none";

    // Label cell
    const labelStyle = {
      ...SL,
      padding: `${isKeyLine ? 10 : 6}px 12px`,
      paddingLeft: `${12 + indent}px`,
      fontSize: isTopLevel && (isHeader || isBoldRow || isCalc) ? "11px" : "10px",
      fontWeight: (isHeader || isBoldRow || isCalc || isKeyLine) ? 700 : 400,
      fontFamily: FONT,
      color: isHeader ? "#6B7280" : isMargin ? "#8B8FA3" : BLACK,
      textTransform: isHeader && isTopLevel ? "uppercase" : "none",
      letterSpacing: isHeader && isTopLevel ? "0.5px" : "0",
      whiteSpace: "nowrap",
      background: rowBg,
      borderTop, borderBottom,
      cursor: isClickable ? "pointer" : "default",
      userSelect: "none",
    };

    // Value cell style
    const valStyle = (col) => ({
      padding: `${isKeyLine ? 10 : 6}px ${viewMode === "monthly" ? 4 : 8}px`,
      textAlign: "right",
      fontFamily: MONO,
      fontSize: isMargin ? "9px" : isKeyLine ? "11px" : "10px",
      fontWeight: (isBoldRow || isCalc || isKeyLine) ? 700 : 400,
      color: isMargin ? "#8B8FA3" : colColor(col),
      background: rowBg,
      borderTop, borderBottom,
      whiteSpace: "nowrap",
      borderRight: isYearEnd(col) ? "2px solid rgba(61,133,198,0.15)" : "none",
    });

    const handleClick = () => {
      if (isHeader) toggle(line.id);
      else if (isTotal && matchingHeaderId) toggle(matchingHeaderId);
    };

    // Arrow indicator
    const arrow = (isCollapsed) => (
      <span style={{display:"inline-block",width:14,fontSize:10,color:"#B0B3C8",transition:"transform 0.15s",transform:isCollapsed?"rotate(-90deg)":"rotate(0deg)"}}>▾</span>
    );

    if (isMargin) {
      return (
        <tr key={line.id}>
          <td style={labelStyle}>{line.label}</td>
          {columns.map(col => (
            <td key={col.key} style={valStyle(col)}>{aggPct(line.numId, line.denId, col.months)}</td>
          ))}
        </tr>
      );
    }

    return (
      <tr key={line.id}>
        <td style={labelStyle} onClick={isClickable ? handleClick : undefined}>
          {isHeader && arrow(collapsed.has(line.id))}
          {isTotal && collapsedParentId && arrow(true)}
          {line.label}
        </td>
        {columns.map(col => (
          <td key={col.key} style={valStyle(col)}>{isHeader ? "" : fmtK(aggVal(line.id, col.months))}</td>
        ))}
      </tr>
    );
  };

  // ★ Item-5 READER — whole-model empty state before the loading/spinner guard, so
  // the no_horizon case shows the message instead of a permanent spinner and the
  // blank-table cases show it instead of empty tables.
  /* ★ CRASHROOT PART 2 — these two returns read `modelState`, the provider's single
     terminal-exit answer, instead of inferring the model's state from onboardingGap
     alone. 'empty' REUSES the existing OnboardingIncomplete JSX unchanged (the gap
     object is still what names it); 'error' is the state that previously had NO
     reader at all — a thrown fetch cleared `loading` with allMonths===[] and this
     page spun forever (reproduced pre-fix in diffs/_crashroot-verifyA-pre.txt).
     ⛔ The `!allMonths.length` guard below STAYS, and stays BELOW these returns:
        belt AND braces, and above them it would send a named-empty tenant back to a
        spinner (diffs/crashroot-phase0.txt §4). */
  if (modelState === 'error') return <ModelLoadError message={modelError} />;
  if (modelState === 'empty') return <OnboardingIncomplete gap={onboardingGap} />;
  if (modelState === 'loading' || loading || !allMonths.length) return <div style={{padding:"60px",textAlign:"center",color:"#8B8FA3",fontFamily:"'DM Sans',sans-serif"}}>Loading income statement...</div>;

  return (
    <div style={{fontFamily:FONT,background:"#F0F1F5",height:"100%",boxSizing:"border-box",padding:"24px",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet"/>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexShrink:0}}>
        <div>
          <h1 style={{margin:0,fontSize:22,fontWeight:700,color:BLACK,letterSpacing:"-0.5px"}}>Income Statement</h1>
          {/* THE LENGTH CHECK COMES FIRST — the KPI treatment (b96e9d5), applied to the sibling
    this line was copy-pasted from. It used to open with getYear(...) and only then test
    the RESULT for null, which is a guard on the call that throws, i.e. no guard at all.
    The page gate above already stops an empty window reaching here; the line is now
    correct ON ITS OWN so it does not depend on a guard hundreds of lines away. Same
    output for a real window. */}
          <p style={{margin:"4px 0 0",fontSize:12,color:"#8B8FA3"}}>{companyName||"—"} — P&L • {(()=>{if(!allMonths.length)return"—";const a=getYear(allMonths[0]);const b=getYear(allMonths[allMonths.length-1]);return a==null?"—":a===b?String(a):`${a}–${b}`;})()}</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {/* View mode segmented toggle */}
          <div style={{display:"flex",borderRadius:6,border:"1px solid #D1D5DB",overflow:"hidden"}}>
            {[["monthly","Monthly"],["quarterly","Quarterly"],["annual","Annual"]].map(([val,label]) => (
              <button key={val} onClick={()=>setViewMode(val)} style={{
                padding:"6px 14px",fontSize:11,fontWeight:600,cursor:"pointer",border:"none",
                background:viewMode===val?"#E8EAFF":"#fff",
                color:viewMode===val?"#3D5AF1":"#4B5563",
                borderRight:val!=="annual"?"1px solid #D1D5DB":"none",
              }}>{label}</button>
            ))}
          </div>
          <div style={{width:1,height:24,background:"#E2E5F0"}}/>
          <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff"}}>
            <span style={{fontSize:10,fontWeight:600,color:"#6B7280",whiteSpace:"nowrap"}}>Last Closed Month</span>
            {/* READOUT, not a control. See BalanceSheetPage for the reasoning. */}
            <span style={{fontSize:11,fontWeight:700,color:BLACK,padding:"2px 4px"}}>{formatMonthLabel(cutoff, "shortFull")}</span>
          </div>
          <div style={{width:1,height:24,background:"#E2E5F0"}}/>
          <button onClick={()=>setCollapsed(new Set(allLines.filter(l=>l.type==="header").map(l=>l.id)))} style={{padding:"6px 14px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:"#4B5563"}}>Collapse All</button>
          <button onClick={()=>setCollapsed(new Set())} style={{padding:"6px 14px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:"#4B5563"}}>Expand All</button>
        </div>
      </div>

      {/* Table */}
      <div style={{background:"#fff",borderRadius:12,border:"1px solid #E2E5F0",overflow:"auto",flex:1,minHeight:0}}>
        <table ref={tableRef} style={{borderCollapse:"separate",borderSpacing:0,width:"max-content",minWidth:"100%"}}>
          <thead style={{position:"sticky",top:0,zIndex:10}}>
            {/* Year row */}
            <tr style={{background:"#FAFBFE"}}>
              <th style={{...SL,padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"#A0A3B8",background:"#FAFBFE",zIndex:12}}></th>
              {yearBands.map((b, i) => {
                const y = b.year;
                const firstM = b.months[0], lastM = b.months[b.months.length - 1];
                const allActuals = lastM <= cutoff;
                const allForecast = firstM > cutoff;
                const label = allActuals ? "ACTUALS" : allForecast ? "FORECAST" : "ACTUALS / FORECAST";
                const color = allForecast ? "#3D85C6" : BLUE;
                const colsInYear = columns.filter(c => c.year === y).length;
                return (
                  <th key={y} colSpan={colsInYear} style={{padding:"6px",textAlign:"center",fontSize:10,fontWeight:700,color,background:"#FAFBFE",borderRight:i!==yearBands.length-1?"2px solid rgba(61,133,198,0.15)":"none"}}>
                    {y} — {label}
                  </th>
                );
              })}
            </tr>
            {/* Column header row */}
            <tr style={{background:"#fff",borderBottom:"2px solid #E2E5F0"}}>
              <th style={{...SL,padding:"8px 12px",textAlign:"left",fontSize:9,fontWeight:600,color:"#8B8FA3",minWidth:220,background:"#fff",zIndex:12}}>Line Item</th>
              {columns.map(col => (
                <th key={col.key} style={{padding:"8px 4px",textAlign:"center",fontSize:9,fontWeight:600,color:colColor(col),whiteSpace:"nowrap",minWidth:viewMode==="monthly"?72:viewMode==="quarterly"?90:110,borderRight:isYearEnd(col)?"2px solid rgba(61,133,198,0.15)":"none"}}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allLines.map(line => renderRow(line))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
